import {Spec} from 'immutability-helper';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import AppDispatcher from 'lib/Dispatcher';
import {Document, FromServerDocument} from 'notebook/documents/DocumentConstants';
import DocumentDAO, {DocumentsByVersion} from 'notebook/documents/DocumentDAO';
import * as DocumentEvents from 'notebook/documents/DocumentEvents';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import * as net from 'util/Net';
import pick from 'util/pick';

export interface DocumentUpdateOptions {
  structuredTableMappings?: Array<StructuredTableMapping>;
  updatedItemPartIndexes?: Array<number>;
  canIncrementalSave?: boolean;
  performTableEvaluation?: boolean;
}

const DocumentActions = {
  close(documentId: string) {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_CLOSE,
      documentId,
    });
  },

  // Returns true if the document is successfully fetched from the server, false if the server returns an error.
  async open(documentId: string): Promise<boolean> {
    let didSucceed = false;
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_OPEN,
      documentId,
    });
    try {
      const document = await Promise.resolve(DocumentDAO.get(documentId));
      AppDispatcher.dispatch({
        actionType: DocumentEvents.DOCUMENT_OPEN_COMPLETED,
        documentId,
        document,
      });
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: DocumentEvents.DOCUMENT_OPEN_FAILED,
          documentId,
        });
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  save(documentId: string): JQueryPromise<boolean> {
    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    // If a save handler is provided to the Document ex: Via StepConfigEditor, use the save provided
    // rather than utilizing the Document PATCH route
    const saveImmediately = DocumentStore.getCustomSaveHandler(documentId);
    if (saveImmediately) {
      DocumentStore.resetSaveTracker(documentId);
      saveImmediately()
        .then(() => {
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .catch((error) => {
          DocumentStore.markAllForUpdate(documentId);
          notifications.notifyError(error);
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        });
      return deferredPromise.promise();
    }
    // Saves the open document to the server.

    // This is saved here in case the document is removed from store before save finishes.
    // TODO(T1847): remove after adding refcounts
    const dataAtSaveTime = DocumentStore.getToServerDocument(documentId);

    benchlingUtil.asyncThrottle(`document-save-${documentId}`, () => {
      let dataToUse;
      if (DocumentStore.inStore(documentId)) {
        if (!DocumentStore.hasUnsavedChanges(documentId)) {
          return deferredPromise.resolve(false).promise();
        }
        dataToUse = DocumentStore.getToServerDocument(documentId);
      } else {
        dataToUse = dataAtSaveTime;
      }

      const {toServerDocument, openVersion} = dataToUse;
      AppDispatcher.dispatch({
        actionType: DocumentEvents.DOCUMENT_SAVE,
        documentId,
      });
      const xhr = DocumentDAO.update(documentId, toServerDocument)
        .done((response) => {
          if (!response) {
            // Resolve the deferred promise to signal that the save has failed.
            deferredPromise.resolve(false);
            return;
          }
          AppDispatcher.dispatch({
            actionType: DocumentEvents.DOCUMENT_SAVE_COMPLETED,
            documentId,
            savedVersion: openVersion,
            versionId: response.cidIdMap[toServerDocument.documentVersion.cid],
            cidIdMap: response.cidIdMap,
            cidNotePartProvenanceIdMap: response.cidNotePartProvenanceIdMap,
            lock: response.lock,
            serverData: {},
          });
          if (DocumentStore.inStore(documentId)) {
            DocumentActions.fetchVersions(documentId);
          }
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .fail((response) => {
          const friendlyMessage = notifications.getFriendlyError(response);
          if (friendlyMessage != null) {
            notifications.error(friendlyMessage, {id: 'document-save-fail', duration: 0});
          }
          AppDispatcher.dispatch({
            actionType: DocumentEvents.DOCUMENT_SAVE_FAILED,
            documentId,
            error: response,
          });
          DocumentStore.markAllForUpdate(documentId);
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        });
      return xhr;
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },

  registerObjects(documentId: string, cidIdMap: CidIdMap) {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_REGISTER_OBJECTS,
      documentId,
      cidIdMap,
    });
  },

  /** Directly updates the note of a given Document.
   *
   * If Table Mappings are provided, they will overwrite the existing ones.
   */
  updateNote(
    documentId: string,
    note: EntryNote,
    {
      structuredTableMappings,
      updatedItemPartIndexes,
      canIncrementalSave,
      performTableEvaluation,
    }: DocumentUpdateOptions = {}
  ) {
    DocumentActions.update(
      documentId,
      DocumentUtil.entryVersionUpdate({
        $merge: {
          note,
          ...(structuredTableMappings && {
            structuredTableMappings,
          }),
        },
      }),
      {updatedItemPartIndexes, cidIdMap: null, bumpVersion: true, performTableEvaluation, canIncrementalSave}
    );
  },

  setName(documentId: string, name: string) {
    DocumentActions.update(documentId, {$merge: {name}});
  },

  /**
   * Revert the document to a previous state
   */
  revert: (documentId: string, op: Spec<Document>, restoreData: SelectionRestoreData): void => {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_REVERT,
      documentId,
      op,
      restoreData,
    });
  },

  loadVersion(documentId: string, versionId: number) {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_LOAD_VERSION,
      documentId,
      versionId,
    });
    DocumentDAO.getVersion(documentId, versionId).then(
      (response: FromServerDocument) => {
        AppDispatcher.dispatch({
          actionType: DocumentEvents.DOCUMENT_LOAD_VERSION_COMPLETED,
          documentId,
          versionId,
          version: response,
        });
      },
      () => {
        AppDispatcher.dispatch({
          actionType: DocumentEvents.DOCUMENT_LOAD_VERSION_FAILED,
          documentId,
          versionId,
        });
      }
    );
  },

  fetchVersions(documentId: string) {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_FETCH_VERSIONS,
      documentId,
    });
    if (DocumentStore.inStore(documentId)) {
      DocumentDAO.getVersions(documentId).then(
        (response: DocumentsByVersion) => {
          AppDispatcher.dispatch(
            // eslint-disable-next-line no-restricted-syntax
            _.extend(
              {
                actionType: DocumentEvents.DOCUMENT_FETCH_VERSIONS_COMPLETED,
                documentId,
                versions: response.data,
              },
              pick(response, 'parent')
            )
          ); // parent key present with null value: the parent was deleted
          // omitted parent key: there is no parent.
        },
        () => {
          AppDispatcher.dispatch({
            actionType: DocumentEvents.DOCUMENT_FETCH_VERSIONS_FAILED,
            documentId,
          });
        }
      );
    } else {
      AppDispatcher.dispatch({
        actionType: DocumentEvents.DOCUMENT_FETCH_VERSIONS_FAILED,
        documentId,
      });
    }
  },

  /**
   * Applies `op` to the document with id `documentId`, and merges in the specified `cidIdMap`.
   * If `op` introduces new models, it should specify the ids in `cidIdMap`.
   */
  update(
    documentId: string,
    op: Spec<Document>,
    param?: {
      cidIdMap: CidIdMap | null;
      bumpVersion: boolean | null;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      performTableEvaluation?: boolean;
    }
  ) {
    // Attempt to acquire an editor lock prior to persisting the new document version
    DocumentEditorLockUtil.acquireLock(documentId);

    if (param == null) {
      param = {cidIdMap: null, bumpVersion: null};
    }
    const {cidIdMap, bumpVersion} = param;
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_UPDATE,
      documentId,
      op,
      cidIdMap: cidIdMap != null ? cidIdMap : {},
      bumpVersion: bumpVersion != null ? bumpVersion : true,
      updatedItemPartIndexes: param?.updatedItemPartIndexes,
      canIncrementalSave: param?.canIncrementalSave,
      performTableEvaluation: param?.performTableEvaluation,
    });
  },

  postServerUpdate: (documentId: string, data: Partial<Document>): void => {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_UPDATE,
      documentId,
      op: {$merge: data},
      bumpVersion: false,
    });
  },

  postServerSideUpdate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_SERVER_SIDE_UPDATE,
      documentId,
      bumpVersion: false,
    });
  },

  invalidate(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_INVALIDATE,
      documentId,
    });
  },

  revalidate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: DocumentEvents.DOCUMENT_OPEN_FAILED,
      documentId,
      // Since revalidate is called when an ActionFunction is aborted, the document will not be out of date,
      // unlike when we dispatch the same event when failing to reload or open the document.
      clearDirtyState: true,
    });
  },

  notifySaveStart: (updates: Array<{documentId: string}>): void => {
    for (const {documentId} of updates) {
      AppDispatcher.dispatch({
        actionType: DocumentEvents.DOCUMENT_SAVE,
        documentId,
      });
    }
  },

  notifySaveComplete: (
    updates: Array<{documentId: string; openVersion: number; documentVersionCid: string}>,
    cidIdMap: CidIdMap
  ): void => {
    for (const {documentId, openVersion, documentVersionCid} of updates) {
      AppDispatcher.dispatch({
        actionType: DocumentEvents.DOCUMENT_SAVE_COMPLETED,
        documentId,
        savedVersion: openVersion,
        versionId: cidIdMap[documentVersionCid],
        cidIdMap,
        serverData: {},
      });
    }
  },
};

export default DocumentActions;
